import {createSelector} from 'reselect';
import {hasQueuedExports} from './has-queued-exports';
// @ts-ignore
import {REDUCER_KEY, SessionModuleState} from './session-types';

const EXPORT_DEFAULT = 'export';
const EXPORT_LEADS = 'lead export';
const EXPORT_COMPANIES = 'company export';
const EXPORT_PEOPLE_API_TYPE = 'contacts export';
const EXPORT_COMPANIES_API_TYPE = 'accounts export';
const LIST_DELETE = 'list delete';

const validNotificationTypes = [
    EXPORT_DEFAULT,
    EXPORT_LEADS,
    EXPORT_COMPANIES,
    EXPORT_COMPANIES_API_TYPE,
    EXPORT_PEOPLE_API_TYPE,
    LIST_DELETE,
];

/**
 * Returns the session-relevant substate of the larger application state.
 *
 * @param {object} state The full application state object
 *
 * @return {object} The state object that relates to our larger application
 *   session, like which user is logged in. Note, this is an area where it would
 *   be nice to have flow's tooling set up so I could describe the structure of
 *   the session type. See the session-reducer to view the `defaultSessionState`
 *   to get a feel for the type's structure.
 */
export const getSessionState = (state: SessionModuleState) => state[REDUCER_KEY];

/**
 * Returns the unique identifier for the session state's user object.
 *
 * @param {object} sessionState The full application state object
 *
 * @return {?string} The opqaue unique identifier for the logged-in user of our
 *   session state object, or null if it's not available.
 */
export const getSessionStateUserId = createSelector([getSessionState], (sessionState) => {
    return sessionState && sessionState.user ? sessionState.user.id : null;
});

export const getSessionStateUserName = createSelector([getSessionState], (sessionState) => {
    return sessionState && sessionState.user ? sessionState.user.name : null;
});

export const getSessionStateUser = createSelector([getSessionState], (sessionState) => {
    return sessionState && sessionState.user ? sessionState.user : null;
});

const getNotifyMessages = createSelector([getSessionStateUser], (sessionStateUser) => {
    if (!sessionStateUser) return [];

    return sessionStateUser.notifyMessages || [];
});

export const getUserPermissions = createSelector([getSessionStateUser], (sessionStateUser) => {
    return sessionStateUser ? sessionStateUser.permissions : null;
});

export const getIsMainSidebarCollapsed = createSelector(
    [getSessionStateUser],
    (sessionStateUser) => {
        return Boolean(sessionStateUser && sessionStateUser.isMainSidebarCollapsed);
    }
);

export const getShouldShowExploreAttentionBadge = createSelector(
    [getSessionStateUser],
    (sessionStateUser) => {
        return Boolean(sessionStateUser && sessionStateUser.shouldShowExploreAttentionBadge);
    }
);

/**
 * Returns a boolean of whether or not the current session user has any queued
 * (incomplete and unacknowledged) exports.
 *
 * @param {object} sessionStateUser The full logged-in-user model object
 *
 * @return {boolean} Does that user have queued exports?
 */
export const getSessionStateUserHasQueuedExports = createSelector(
    [getNotifyMessages],
    (notifyMessages) => {
        return hasQueuedExports(notifyMessages);
    }
);

/**
 * Returns a filtered list of notifyMessages pertaining to the logged-in-user.
 * This notifyMessages are completed notifications (i.e. finished exports), that
 * have _not yet_ been acknowledged by the user.
 *
 * As an example, a completed export that has not been downloaded nor dismissed
 * would be returned here.
 *
 * @param {object} sessionStateUser The full logged-in-user model object
 *
 * @return {object[]} Unacknowledged list of notifications
 */
export const getSessionStateUserUnacknowedgedExportNotifications = createSelector(
    [getNotifyMessages],
    (notifyMessages) => {
        return notifyMessages.filter((notify) => {
            return (
                validNotificationTypes.includes(notify.type) &&
                (notify.status === 'complete' || !notify.acknowledged)
            );
        });
    }
);
